/* hs-eslint ignored failing-rules */

/* eslint-disable no-bitwise */
'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { PureComponent } from 'react';
import { findDOMNode } from 'react-dom';
import { debounce } from 'underscore';
import EditorError from 'DesignData/models/validation/EditorError';
import ErrorMessage from 'DesignComponents/statusBar/ErrorMessage';
import { AutoSizer, List } from 'react-virtualized';
const lineHeightInPx = parseInt(getComputedStyle(document.documentElement).fontSize, 10) * 1.5;
const firstItemTopPadding = 16;
let calculatedFontWidth = null;

function getIntegerLength(x) {
  if (x == null) return 0;
  if (x < 0) x = -x; // This would occur if number is greater than max uint32
  // Users put minified libraries in code files so column numbers can get big.

  if (x !== x >>> 0) return 20;
  let cnt = 0;

  do {
    if (x < 10) return 1 + cnt;
    if (x < 100) return 2 + cnt;
    if (x < 1000) return 3 + cnt;
    if (x < 10000) return 4 + cnt;
    ++cnt;
    x = x / 10 >>> 0;
  } while (x);

  return cnt;
}

export default class MessagesList extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      width: 0
    };
    this.onRowsRendered = debounce(({
      overscanStartIndex,
      overscanStopIndex
    }) => {
      this.renderedStartIndex = overscanStartIndex;
      this.renderedStopIndex = overscanStopIndex;
      const width = this.fuzzyCalculateWidth(this.props.list, overscanStartIndex, overscanStopIndex);
      this.trySetWidth(width);
    }, 200);

    this.rowHeight = ({
      index
    }) => {
      let height = 0;

      if (index === 0) {
        height += firstItemTopPadding;
      }

      const error = this.props.list[index];

      if (error.helpMessage) {
        height += lineHeightInPx * 2 + 12;
      } else {
        height += lineHeightInPx + 12;
      }

      return height;
    };

    this.renderMessage = ({
      index,
      key,
      style
    }) => {
      const css = Object.assign({}, style, {
        whiteSpace: 'nowrap'
      });

      if (index === 0) {
        css.paddingTop = `${firstItemTopPadding}px`;
      }

      const error = this.props.list[index];
      return /*#__PURE__*/_jsx(ErrorMessage, {
        error: error,
        errorNumber: index + 1,
        style: css
      }, key);
    };

    this.renderedStartIndex = 0;
    this.renderedStopIndex = Math.min(props.list.length, MessagesList.DEFAULT_WIDTH_SCAN_COUNT);

    const _width = this.fuzzyCalculateWidth(props.list, this.renderedStartIndex, this.renderedStopIndex);

    this.state.width = _width;
  }

  componentDidMount() {
    this.calculateFontWidth();
  }

  componentDidUpdate(prevProps) {
    const {
      list
    } = prevProps;

    if (list !== this.props.list) {
      this.onNewListProp(this.props.list);
    }

    this.calculateFontWidth();
  }

  get fontWidth() {
    return calculatedFontWidth || MessagesList.FUZZY_MESSAGE_FONT_WIDTH;
  }

  calculateFontWidth() {
    if (calculatedFontWidth) return calculatedFontWidth;
    const el = findDOMNode(this);
    if (!el) return null;
    const messageEl = el.querySelector('.error-message');
    if (!messageEl) return null;
    calculatedFontWidth = parseInt(getComputedStyle(messageEl).fontSize, 10) * 0.66 | 0;
    return calculatedFontWidth;
  }

  fuzzyCalculateWidth(list, startIndex, stopIndex) {
    const fontWidth = this.fontWidth;
    const iconSize = 30;
    const messageType = 100;
    const extraPadding = 50;
    const fixedSizes = iconSize + messageType + extraPadding;
    let max = 0;

    for (let i = startIndex; i <= stopIndex; ++i) {
      const error = list[i];
      if (!error) continue;
      let size = fixedSizes;
      const {
        rawMessage,
        helpMessage,
        message,
        lineNumber,
        column
      } = error;
      size += getIntegerLength(i) * fontWidth; // List item number

      size += getIntegerLength(lineNumber) * fontWidth;
      size += getIntegerLength(column) * fontWidth;
      size += Math.max(message ? message.length : 0, rawMessage ? rawMessage.length : 0, helpMessage ? helpMessage.length : 0) * fontWidth;

      if (size > max) {
        max = size;
      }
    }

    return max | 0;
  }

  trySetWidth(width) {
    const {
      WIDTH_MARGIN_OF_ERROR
    } = MessagesList;

    if (width > this.state.width + WIDTH_MARGIN_OF_ERROR || width < this.state.width - WIDTH_MARGIN_OF_ERROR) {
      this.setState({
        width
      });
    }
  }

  onNewListProp(list) {
    if (this.renderedStopIndex >= list.length) {
      // This will happen when the new list is shorter than the old
      let start = this.renderedStartIndex;
      let stop = this.renderedStopIndex;
      const range = stop - start;
      stop = list.length - 1;

      if (range > list.length) {
        start = 0;
      } else {
        start = stop - range;
      }

      this.renderedStartIndex = start;
      this.renderedStopIndex = stop;
    }

    const width = this.fuzzyCalculateWidth(list, this.renderedStartIndex, this.renderedStopIndex);
    this.trySetWidth(width);
  }

  render() {
    return /*#__PURE__*/_jsx(AutoSizer, {
      disableHeight: true,
      children: ({
        width
      }) => /*#__PURE__*/_jsx(List, {
        height: 200,
        width: this.state.width > width ? this.state.width : width,
        rowCount: this.props.list.length,
        rowHeight: this.rowHeight,
        rowRenderer: this.renderMessage,
        overscanRowCount: 5,
        onRowsRendered: this.onRowsRendered
      })
    });
  }

}
MessagesList.propTypes = {
  list: EditorError.editorErrorArrayPropType.isRequired
};
MessagesList.WIDTH_MARGIN_OF_ERROR = 50;
MessagesList.DEFAULT_WIDTH_SCAN_COUNT = 20;
MessagesList.FUZZY_MESSAGE_FONT_WIDTH = 8;